/**
 * Handles blog browse pagination and search filtering from URL params.
 * Shopify Liquid cannot reliably read query params due to page caching.
 *
 * Uses "blog_page" (not "page") for pagination, because Shopify's paginate
 * tag reserves the "page" query param and returns no articles on page 2+.
 */
(function () {
  const PAGE_PARAM = 'blog_page';

  function redirectLegacyPageParam() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('page')) return false;

    params.set(PAGE_PARAM, params.get('page'));
    params.delete('page');
    const query = params.toString();
    window.location.replace(query ? `${window.location.pathname}?${query}` : window.location.pathname);
    return true;
  }

  if (redirectLegacyPageParam()) return;

  function getParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      query: params.get('q')?.trim() || '',
      page: Math.max(1, parseInt(params.get(PAGE_PARAM) || '1', 10) || 1),
    };
  }

  function decodeHtml(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function formatResultsCount(count, terms, container) {
    let template = decodeHtml(container.dataset.resultsCountOther || '__COUNT__ results for __TERMS__');
    if (count === 1 && container.dataset.resultsCountOne) {
      template = decodeHtml(container.dataset.resultsCountOne);
    }

    return template.replace('__COUNT__', String(count)).replace('__TERMS__', terms);
  }

  function formatNoResultsMessage(query, container) {
    const template = decodeHtml(container.dataset.noResultsMessage || 'No results for "__TERMS__". Try a different search term.');
    return template.replace('__TERMS__', query);
  }

  function buildPageUrl(blogUrl, query, page) {
    const url = new URL(blogUrl, window.location.origin);
    if (query) url.searchParams.set('q', query);
    if (page > 1) url.searchParams.set(PAGE_PARAM, String(page));
    return `${url.pathname}${url.search}`;
  }

  function renderPagination(container, currentPage, totalPages, query, blogUrl) {
    const paginationHost = container.querySelector('[data-blog-search-pagination]');
    if (!paginationHost) return;

    if (totalPages <= 1) {
      paginationHost.innerHTML = '';
      return;
    }

    const nav = document.createElement('nav');
    nav.className = 'blog-search-pagination pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', container.dataset.paginationLabel || 'Pagination');

    if (currentPage > 1) {
      const prev = document.createElement('a');
      prev.className = 'pagination__item group';
      prev.rel = 'prev';
      prev.href = buildPageUrl(blogUrl, query, currentPage - 1);
      prev.innerHTML = '<span class="animated-arrow animated-arrow--reverse"></span>';
      nav.appendChild(prev);
    } else {
      const prev = document.createElement('span');
      prev.className = 'pagination__item pagination__item--disabled';
      prev.innerHTML = '<span class="animated-arrow animated-arrow--reverse"></span>';
      nav.appendChild(prev);
    }

    const current = document.createElement('span');
    current.className = 'pagination__current';
    current.innerHTML = `${currentPage}&nbsp;&nbsp;/&nbsp;&nbsp;${totalPages}`;
    nav.appendChild(current);

    if (currentPage < totalPages) {
      const next = document.createElement('a');
      next.className = 'pagination__item group';
      next.rel = 'next';
      next.href = buildPageUrl(blogUrl, query, currentPage + 1);
      next.innerHTML = '<span class="animated-arrow"></span>';
      nav.appendChild(next);
    } else {
      const next = document.createElement('span');
      next.className = 'pagination__item pagination__item--disabled';
      next.innerHTML = '<span class="animated-arrow"></span>';
      nav.appendChild(next);
    }

    paginationHost.innerHTML = '';
    paginationHost.appendChild(nav);
  }

  function getSearchRelevance(card, queryLower) {
    const title = card.dataset.searchTitle || '';
    if (title.includes(queryLower)) {
      return { tier: 0, index: title.indexOf(queryLower) };
    }
    return { tier: 1, index: -1 };
  }

  function sortByRelevance(cards, queryLower) {
    return cards.sort((a, b) => {
      const aRelevance = getSearchRelevance(a, queryLower);
      const bRelevance = getSearchRelevance(b, queryLower);

      if (aRelevance.tier !== bRelevance.tier) {
        return aRelevance.tier - bRelevance.tier;
      }

      if (aRelevance.tier === 0 && aRelevance.index !== bRelevance.index) {
        return aRelevance.index - bRelevance.index;
      }

      return 0;
    });
  }

  function updateBreadcrumbs(query, blogUrl) {
    const blogLink = document.querySelector('[data-breadcrumb-blog-link]');
    const searchItem = document.querySelector('[data-breadcrumb-search-item]');
    const searchText = document.querySelector('[data-breadcrumb-search-text]');
    const searchLink = document.querySelector('[data-breadcrumb-search-link]');
    const breadcrumbsNav = document.querySelector('[data-breadcrumb-search-label]');

    if (!blogLink || !searchItem) return;

    if (query) {
      blogLink.removeAttribute('aria-current');
      searchItem.hidden = false;
      const labelTemplate = decodeHtml(breadcrumbsNav?.dataset.breadcrumbSearchLabel || 'Search: results for __TERMS__');
      searchText.textContent = labelTemplate.replace('__TERMS__', query);
      searchLink.href = `${blogUrl}?q=${encodeURIComponent(query)}`;
      searchLink.setAttribute('aria-current', 'page');
    } else {
      blogLink.setAttribute('aria-current', 'page');
      searchItem.hidden = true;
      searchLink.removeAttribute('aria-current');
    }
  }

  function setRevealState(element, visible) {
    if (!element) return;

    if (visible) {
      element.hidden = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          element.classList.add('is-visible');
        });
      });
    } else {
      element.classList.remove('is-visible');
      const duration = element.matches('[data-blog-search-empty]') ? 500 : 400;
      window.setTimeout(() => {
        if (!element.classList.contains('is-visible')) {
          element.hidden = true;
        }
      }, duration);
    }
  }

  function revealCards(cards, startIndex, endIndex, animate) {
    cards.forEach((card, index) => {
      const isOnPage = index >= startIndex && index < endIndex;

      card.classList.remove('is-visible');

      if (isOnPage) {
        card.hidden = false;
        if (animate) {
          const staggerIndex = index - startIndex;
          window.setTimeout(() => {
            if (!card.hidden) {
              card.classList.add('is-visible');
            }
          }, staggerIndex * 45);
        } else {
          card.classList.add('is-visible');
        }
      } else {
        card.hidden = true;
      }
    });
  }

  function applyBlogSearchResults() {
    const container = document.querySelector('.blog-posts[data-blog-search-results]');
    if (!container) return;

    const perPage = parseInt(container.dataset.articlesPerPage || '18', 10);
    const blogUrl = container.dataset.blogUrl || window.location.pathname;
    const { query, page: requestedPage } = getParams();
    const cards = [...container.querySelectorAll('blog-post-card[data-search-content]')];
    const heading = container.querySelector('[data-blog-search-heading]');
    const headingText = heading?.querySelector('.h4, p') || heading;
    const emptyState = container.querySelector('[data-blog-search-empty]');
    const emptyMessage = container.querySelector('[data-blog-search-empty-message]');

    document.querySelectorAll('blog-search input[name="q"]').forEach((input) => {
      input.value = query;
    });

    let matchingCards = cards;
    if (query) {
      const queryLower = query.toLowerCase();
      matchingCards = cards.filter((card) => (card.dataset.searchTitle || '').includes(queryLower));
      matchingCards = sortByRelevance(matchingCards, queryLower);
    }

    const totalPages = Math.max(1, Math.ceil(matchingCards.length / perPage));
    const currentPage = Math.min(requestedPage, totalPages);
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;

    cards.forEach((card) => {
      card.classList.remove('is-visible');
      card.hidden = true;
    });

    revealCards(matchingCards, startIndex, endIndex, Boolean(query));

    if (heading && headingText) {
      if (query && matchingCards.length > 0) {
        headingText.textContent = formatResultsCount(matchingCards.length, query, container);
        setRevealState(heading, true);
        setRevealState(emptyState, false);
      } else {
        setRevealState(heading, false);
      }
    }

    if (emptyState && emptyMessage) {
      if (query && matchingCards.length === 0) {
        emptyMessage.textContent = formatNoResultsMessage(query, container);
        setRevealState(emptyState, true);
        setRevealState(heading, false);
      } else if (!query || matchingCards.length > 0) {
        setRevealState(emptyState, false);
      }
    }

    renderPagination(container, currentPage, totalPages, query, blogUrl);
    updateBreadcrumbs(query, blogUrl);
    container.classList.toggle('blog-search-results--filtered', Boolean(query));
    container.classList.add('blog-search-results--initialized');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyBlogSearchResults);
  } else {
    applyBlogSearchResults();
  }
})();
